import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../App';
import { useIsMobile } from '../utils';

// #192 D3, punto 6 (corrección) — mismo criterio que en JobModal.js: el
// campo carga en GiB pero el panel/notificaciones muestran GB. Duplicado
// acá (no importado de JobModal) por el mismo motivo que _fmt_bytes_gb en
// run_finalizer.py: no cruzar el límite de módulo por un helper de 1 línea.
function gibToGbHelpText(gibValue) {
  const n = parseFloat(gibValue);
  if (!gibValue || Number.isNaN(n) || n <= 0) return null;
  const gb = (n * 1024 ** 3) / 1e9;
  return `${n} GiB ≈ ${gb.toFixed(2)} GB`;
}

// #184 c / #189 — línea discreta con el código y el esquema que corren.
export function systemSummaryLine(info) {
  const sha = info.git_sha && info.git_sha !== 'unknown' ? info.git_sha.slice(0, 7) : 'unknown';
  const arranque = info.started_at ? new Date(info.started_at).toLocaleString('es-AR') : '—';
  const applied = (info.alembic_applied || []).join(', ') || '—';
  const heads = (info.alembic_heads || []).join(', ') || '—';
  return `Versión ${sha} · arrancó ${arranque} · esquema ${applied}${info.pending ? ` (esperada ${heads})` : ''}`;
}

function Field({ label, type = 'text', value, onChange, placeholder = '' }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}

function Btn({ label, accent, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: accent ? 'var(--accent)' : 'transparent',
      border: `1px solid ${accent ? 'var(--accent)' : 'var(--border)'}`,
      color: accent ? 'var(--bg)' : 'var(--text)',
      padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)',
      cursor: disabled ? 'wait' : 'pointer', fontWeight: accent ? 700 : 400, marginRight: 8
    }}>{label}</button>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 20 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>{title}</span>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

function PlanModal({ plan, onClose, onSaved }) {
  const isNew = !plan?.id;
  const initTiers = (plan?.storage_tiers && plan.storage_tiers.length)
    ? plan.storage_tiers.map(t => ({ from_gb: t.from_gb, to_gb: t.to_gb, price_per_gb: t.price_per_gb }))
    : [{ from_gb: plan?.included_storage_gb || 1024, to_gb: null, price_per_gb: 0 }];
  const [form, setForm] = useState({
    name:                       plan?.name || '',
    included_storage_gb:        plan?.included_storage_gb || 1024,
    included_servers:           plan?.included_servers || 1,
    retention_days:             plan?.retention_days || 30,
    base_price_usd:             plan?.base_price_usd ?? 0,
    price_per_extra_server_usd: plan?.price_per_extra_server_usd ?? 0,
    price_per_cloud_user_usd:   plan?.price_per_cloud_user_usd ?? 0,
    covert_copy_price_usd:      plan?.covert_copy_price_usd ?? 0,
    active:                     plan?.active ?? true,
  });
  const [tiers, setTiers] = useState(initTiers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // from_gb del primer tramo = included; los demás encadenan con el to_gb anterior; último abierto.
  const normalizeTiers = (arr, included) => {
    const out = arr.map(t => ({ ...t }));
    for (let i = 0; i < out.length; i++) {
      out[i].from_gb = i === 0 ? included : (out[i - 1].to_gb ?? included);
    }
    if (out.length) out[out.length - 1].to_gb = null;
    return out;
  };

  const setTier = (i, k, v) => setTiers(ts => ts.map((t, j) => j === i ? { ...t, [k]: v } : t));
  const addTier = () => setTiers(ts => {
    const last = ts[ts.length - 1];
    const boundary = (parseInt(last.to_gb) || parseInt(last.from_gb) || parseInt(form.included_storage_gb) || 0) + 1024;
    const copy = ts.map(t => ({ ...t }));
    copy[copy.length - 1] = { ...last, to_gb: boundary };
    copy.push({ from_gb: boundary, to_gb: null, price_per_gb: 0 });
    return copy;
  });
  const removeTier = (i) => setTiers(ts => ts.length > 1 ? ts.filter((_, j) => j !== i) : ts);

  const inp = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12, boxSizing: 'border-box', outline: 'none' };

  const submit = async () => {
    if (!form.name) { setError('El nombre es obligatorio'); return; }
    const incomplete = tiers.slice(0, -1).some(t => t.to_gb == null || t.to_gb === '');
    if (incomplete) { setError('Todos los tramos (excepto el último) deben tener un valor "Hasta"'); return; }
    const included = parseInt(form.included_storage_gb) || 0;
    const payloadTiers = normalizeTiers(tiers, included).map(t => ({
      from_gb: parseInt(t.from_gb) || 0,
      to_gb: t.to_gb == null ? null : (parseInt(t.to_gb) || 0),
      price_per_gb: parseFloat(t.price_per_gb) || 0,
    }));
    const payload = {
      ...form,
      included_storage_gb: included,
      included_servers: parseInt(form.included_servers) || 0,
      retention_days: parseInt(form.retention_days) || 0,
      base_price_usd: parseFloat(form.base_price_usd) || 0,
      price_per_extra_server_usd: parseFloat(form.price_per_extra_server_usd) || 0,
      price_per_cloud_user_usd: parseFloat(form.price_per_cloud_user_usd) || 0,
      covert_copy_price_usd: parseFloat(form.covert_copy_price_usd) || 0,
      storage_tiers: payloadTiers,
    };
    setLoading(true);
    try {
      if (isNew) { await api.createPlan(payload); } else { await api.updatePlan(plan.id, payload); }
      onSaved();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const tiersView = normalizeTiers(tiers, parseInt(form.included_storage_gb) || 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>{isNew ? 'NUEVO PLAN' : 'EDITAR PLAN'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto' }}>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          <Field label="Nombre" value={form.name} onChange={v => set('name', v)} placeholder="Starter" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Storage incluido (GB)" type="number" value={form.included_storage_gb} onChange={v => set('included_storage_gb', v)} placeholder="1024" />
            <Field label="Retención (días)" type="number" value={form.retention_days} onChange={v => set('retention_days', v)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Servidores incluidos" type="number" value={form.included_servers} onChange={v => set('included_servers', v)} placeholder="1" />
            <Field label="Precio servidor extra (USD)" type="number" value={form.price_per_extra_server_usd} onChange={v => set('price_per_extra_server_usd', v)} placeholder="5.00" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Precio piso (USD/mes)" type="number" value={form.base_price_usd} onChange={v => set('base_price_usd', v)} placeholder="8.00" />
            <Field label="Covert Copy — cargo fijo gestión (USD/mes)" type="number" value={form.covert_copy_price_usd} onChange={v => set('covert_copy_price_usd', v)} placeholder="0.00" />
          </div>
          <Field label="Precio por usuario cloud (USD/mes)" type="number" value={form.price_per_cloud_user_usd} onChange={v => set('price_per_cloud_user_usd', v)} placeholder="0.00" />
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: -8, marginBottom: 16 }}>
            El piso cubre el storage incluido + servidores incluidos y se cobra siempre. Lo que supera
            el storage incluido se cobra por GB según la escala de abajo (marginal).
          </div>

          {/* Escala de tramos (marginal) */}
          <div style={{ border: '1px solid var(--border)', padding: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 10 }}>Escala de storage (USD/GB sobre el excedente)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 32px', gap: 8, fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 6 }}>
              <span>Desde (GB)</span><span>Hasta (GB)</span><span>$/GB</span><span></span>
            </div>
            {tiersView.map((t, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 32px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                <input value={t.from_gb} disabled style={{ ...inp, opacity: 0.6 }} />
                {i === tiersView.length - 1
                  ? <input value="∞" disabled style={{ ...inp, opacity: 0.6, textAlign: 'center' }} />
                  : <input type="number" value={t.to_gb ?? ''} onChange={e => setTier(i, 'to_gb', e.target.value)} style={inp} />}
                <input type="number" step="0.001" value={t.price_per_gb} onChange={e => setTier(i, 'price_per_gb', e.target.value)} style={inp} />
                {tiersView.length > 1
                  ? <button onClick={() => removeTier(i)} title="Quitar tramo" style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>−</button>
                  : <span />}
              </div>
            ))}
            <button onClick={addTier} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '6px 12px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', marginTop: 4 }}>+ AGREGAR TRAMO</button>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 8 }}>
              Plan solo-piso: un único tramo con $/GB = 0. El primer tramo arranca en el storage incluido.
            </div>
          </div>

          <Select label="Estado" value={form.active ? 'true' : 'false'} onChange={v => set('active', v === 'true')}
            options={[['true', 'Activo'], ['false', 'Inactivo']]} />
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <Btn label="CANCELAR" onClick={onClose} />
          <Btn label={loading ? 'GUARDANDO...' : (isNew ? 'CREAR PLAN' : 'GUARDAR')} accent onClick={submit} disabled={loading} />
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  useAuth();
  const isMobile = useIsMobile();

  const [plans, setPlans]         = useState([]);
  const [planModal, setPlanModal] = useState(null);
  const [heartbeats, setHeartbeats] = useState([]);
  const [saved, setSaved]         = useState('');
  const [testingWasabi, setTestingWasabi] = useState(false);
  const [savingWasabi, setSavingWasabi]   = useState(false);
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [savingSmtp, setSavingSmtp]       = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);

  const [wasabi, setWasabi] = useState({
    access_key: '', secret_key: '', region: 'us-east-1', bucket_prefix: 'taxco-'
  });
  const [telegram, setTelegram] = useState({ bot_token: '', chat_id: '' });
  const [legal, setLegal] = useState({
    privacy_policy_url: '', privacy_policy_version: '', privacy_policy_updated_at: '',
    sla_url: '', sla_version: '', sla_updated_at: '',
  });
  const [savingLegal, setSavingLegal] = useState(false);
  const [smtp, setSmtp] = useState({ host: '', port: '587', user: '', password: '', from: '', use_tls: true });
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [defaults, setDefaults] = useState({ retention_days: 30, backup_hour: '02:00', zombie_timeout_hours: 4, total_storage_gb: 0, daily_summary_hour: '08:00', welcome_email_enabled: false, wasabi_rate_usd_per_tb: '6.99', snapshot_listing_ttl_hours: '48', job_command_stuck_hours: '2', job_command_retention_days: '30', job_run_retention_days: '365', audit_log_retention_days: '730', default_max_duration_minutes: '', initial_load_default_tranche_gib: '', initial_load_max_reasonable_days: '' });
  const [mfaPolicy, setMfaPolicy] = useState({ admin: false, operator: false, client: false, reseller: false });

  const loadPlans = async () => {
    try { setPlans(await api.getPlans()); } catch (e) { console.error(e); }
  };

  const [systemInfo, setSystemInfo] = useState(null);
  const loadSystemInfo = async () => {
    try { setSystemInfo(await api.getSystemInfo()); } catch (e) { console.error(e); }
  };

  const loadHeartbeats = async () => {
    try { setHeartbeats(await api.getTaskHeartbeats()); } catch (e) { console.error(e); }
  };

  useEffect(() => { loadPlans(); loadSettings(); loadHeartbeats(); loadSystemInfo(); }, []);

  const showSaved = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 2500); };
  const [errMsg, setErrMsg]       = useState('');
  const showError = (msg) => { setErrMsg(msg); setTimeout(() => setErrMsg(''), 4000); };

  // F-06: carga default_retention_days y default_backup_hour desde el backend
  const loadSettings = async () => {
    try {
      const s = await api.getSettings();
      setWasabi({ access_key: s.wasabi_access_key || '', secret_key: s.wasabi_secret_key || '', region: s.wasabi_region || 'us-east-1', bucket_prefix: s.wasabi_bucket_prefix || 'taxco-' });
      setTelegram({ bot_token: s.telegram_bot_token || '', chat_id: s.telegram_chat_id || '' });
      setLegal({
        privacy_policy_url:        s.privacy_policy_url || '',
        privacy_policy_version:    s.privacy_policy_version || '',
        privacy_policy_updated_at: s.privacy_policy_updated_at || '',
        sla_url:                   s.sla_url || '',
        sla_version:                s.sla_version || '',
        sla_updated_at:             s.sla_updated_at || '',
      });
      setSmtp({
        host: s.smtp_host || '', port: s.smtp_port || '587',
        user: s.smtp_user || '', password: s.smtp_password || '',
        from: s.smtp_from || '', use_tls: s.smtp_use_tls !== 'false',
      });
      setDefaults({
        retention_days:              parseInt(s.default_retention_days) || 30,
        backup_hour:                 s.default_backup_hour || '02:00',
        zombie_timeout_hours:        parseInt(s.zombie_timeout_hours) || 4,
        total_storage_gb:            parseInt(s.total_storage_gb) || 0,
        daily_summary_hour:          s.daily_summary_hour || '08:00',
        welcome_email_enabled:       s.welcome_email_enabled === 'true',
        wasabi_rate_usd_per_tb:      s.wasabi_rate_usd_per_tb || '6.99',
        snapshot_listing_ttl_hours:  s.snapshot_listing_ttl_hours || '48',
        job_command_stuck_hours:     s.job_command_stuck_hours || '2',
        job_command_retention_days:  s.job_command_retention_days || '30',
        job_run_retention_days:      s.job_run_retention_days || '365',
        audit_log_retention_days:    s.audit_log_retention_days || '730',
        default_max_duration_minutes: s.default_max_duration_minutes || '',
        // #192 D3, 2.d — se guarda en bytes; se muestra convertido a GiB.
        initial_load_default_tranche_gib: s.initial_load_default_tranche_bytes
          ? String(Math.round(parseInt(s.initial_load_default_tranche_bytes, 10) / 1024 ** 3))
          : '',
        initial_load_max_reasonable_days: s.initial_load_max_reasonable_days || '',
      });
      setMfaPolicy({
        admin:    s.mfa_required_admin    === 'true',
        operator: s.mfa_required_operator === 'true',
        client:   s.mfa_required_client   === 'true',
        reseller: s.mfa_required_reseller === 'true',
      });
    } catch (e) { console.error(e); }
  };

  const saveMfaPolicy = async (next) => {
    try {
      await api.updateSettings({
        mfa_required_admin:    String(next.admin),
        mfa_required_operator: String(next.operator),
        mfa_required_client:   String(next.client),
        mfa_required_reseller: String(next.reseller),
      });
      showSaved('Política MFA guardada');
    } catch(e) { showError(e.message); }
  };

  const toggleMfa = (role) => {
    const next = { ...mfaPolicy, [role]: !mfaPolicy[role] };
    setMfaPolicy(next);
    saveMfaPolicy(next);
  };

  const saveWasabi = async () => {
    setSavingWasabi(true);
    try {
      await api.updateSettings({ wasabi_access_key: wasabi.access_key, wasabi_secret_key: wasabi.secret_key, wasabi_region: wasabi.region, wasabi_bucket_prefix: wasabi.bucket_prefix });
      showSaved('Configuración Wasabi guardada');
    } catch(e) { showError(e.message); }
    finally { setSavingWasabi(false); }
  };

  // F-05: llama al backend real en lugar de mostrar OK directamente
  const testWasabi = async () => {
    setTestingWasabi(true);
    try {
      const res = await api.testWasabi();
      if (res.ok) {
        showSaved('Conexión Wasabi OK — ' + res.msg);
      } else {
        showError('Error de conexión: ' + res.msg);
      }
    } catch(e) {
      showError(e.message);
    } finally {
      setTestingWasabi(false);
    }
  };

  const saveTelegram = async () => {
    setSavingTelegram(true);
    try {
      await api.updateSettings({ telegram_bot_token: telegram.bot_token, telegram_chat_id: telegram.chat_id });
      showSaved('Configuración Telegram guardada');
    } catch(e) { showError(e.message); }
    finally { setSavingTelegram(false); }
  };

  const testTelegram = async () => {
    setTestingTelegram(true);
    try {
      await api.testTelegram();
      showSaved('Mensaje de prueba enviado');
    } catch(e) { showError(e.message); }
    finally { setTestingTelegram(false); }
  };

  const saveLegal = async () => {
    setSavingLegal(true);
    try {
      await api.updateSettings({ ...legal });
      showSaved('Configuración legal guardada');
    } catch(e) { showError(e.message); }
    finally { setSavingLegal(false); }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await api.updateSettings({
        smtp_host: smtp.host, smtp_port: String(smtp.port),
        smtp_user: smtp.user, smtp_password: smtp.password,
        smtp_from: smtp.from, smtp_use_tls: String(smtp.use_tls),
      });
      showSaved('Configuración SMTP guardada');
    } catch(e) { showError(e.message); }
    finally { setSavingSmtp(false); }
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await api.testSmtp();
      if (res.ok) showSaved('SMTP OK — ' + res.msg);
      else showError('Error SMTP: ' + res.msg);
    } catch(e) { showError(e.message); }
    finally { setTestingSmtp(false); }
  };

  // F-06: guarda defaults en el backend
  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      await api.updateSettings({
        default_retention_days:      String(defaults.retention_days),
        default_backup_hour:         defaults.backup_hour,
        zombie_timeout_hours:        String(defaults.zombie_timeout_hours),
        total_storage_gb:            String(defaults.total_storage_gb || 0),
        daily_summary_hour:          defaults.daily_summary_hour,
        welcome_email_enabled:       String(defaults.welcome_email_enabled),
        wasabi_rate_usd_per_tb:      String(defaults.wasabi_rate_usd_per_tb || '6.99'),
        snapshot_listing_ttl_hours:  String(defaults.snapshot_listing_ttl_hours || '48'),
        job_command_stuck_hours:     String(defaults.job_command_stuck_hours || '2'),
        job_command_retention_days:  String(defaults.job_command_retention_days || '30'),
        job_run_retention_days:      String(defaults.job_run_retention_days || '365'),
        audit_log_retention_days:    String(defaults.audit_log_retention_days || '730'),
        default_max_duration_minutes: String(defaults.default_max_duration_minutes || ''),
        initial_load_default_tranche_bytes: defaults.initial_load_default_tranche_gib
          ? String(Math.round(Number(defaults.initial_load_default_tranche_gib) * 1024 ** 3))
          : '',
        initial_load_max_reasonable_days: String(defaults.initial_load_max_reasonable_days || ''),
      });
      showSaved('Políticas guardadas');
    } catch(e) { showError(e.message); }
    finally { setSavingDefaults(false); }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Configuración</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>Parámetros globales del sistema</div>
      </div>

      {errMsg && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', fontFamily: 'var(--mono)', fontSize: 12 }}>
          ✕ {errMsg}
        </div>
      )}
      {saved && (
        <div style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid var(--green)', color: 'var(--green)', padding: '10px 16px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 20 }}>
          ✓ {saved}
        </div>
      )}

      {planModal !== null && (
        <PlanModal
          plan={planModal === 'new' ? null : planModal}
          onClose={() => setPlanModal(null)}
          onSaved={() => { loadPlans(); showSaved('Plan guardado correctamente'); }}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
        <div>
          <Card title="Wasabi S3">
            <Field label="Access Key ID" value={wasabi.access_key} onChange={v => setWasabi(w => ({...w, access_key: v}))} placeholder="WABI..." />
            <Field label="Secret Access Key" type="password" value={wasabi.secret_key} onChange={v => setWasabi(w => ({...w, secret_key: v}))} />
            <Select label="Región" value={wasabi.region} onChange={v => setWasabi(w => ({...w, region: v}))}
              options={[['us-east-1','us-east-1 (Virginia)'], ['eu-central-1','eu-central-1 (Frankfurt)'], ['ap-southeast-1','ap-southeast-1 (Singapur)']]} />
            <Field label="Bucket Prefix" value={wasabi.bucket_prefix} onChange={v => setWasabi(w => ({...w, bucket_prefix: v}))} />
            <Btn label={savingWasabi ? 'GUARDANDO...' : 'GUARDAR'} accent onClick={saveWasabi} disabled={savingWasabi} />
            <Btn label={testingWasabi ? 'PROBANDO...' : 'PROBAR CONEXIÓN'} onClick={testWasabi} disabled={testingWasabi} />
          </Card>

          <Card title="Notificaciones Telegram">
            <Field label="Bot Token" value={telegram.bot_token} onChange={v => setTelegram(t => ({...t, bot_token: v}))} placeholder="7483...:AAF..." />
            <Field label="Chat ID Admin" value={telegram.chat_id} onChange={v => setTelegram(t => ({...t, chat_id: v}))} placeholder="-100..." />
            <Btn label={savingTelegram ? 'GUARDANDO...' : 'GUARDAR'} accent onClick={saveTelegram} disabled={savingTelegram} />
            <Btn label={testingTelegram ? 'ENVIANDO...' : 'ENVIAR TEST'} onClick={testTelegram} disabled={testingTelegram} />
          </Card>

          <Card title="Documentos legales — Política de Privacidad y SLA">
            <Field label="URL Política de Privacidad" value={legal.privacy_policy_url}
              onChange={v => setLegal(l => ({...l, privacy_policy_url: v}))} placeholder="https://openbackup.ar/privacidad" />
            <Field label="Versión vigente Política de Privacidad" value={legal.privacy_policy_version}
              onChange={v => setLegal(l => ({...l, privacy_policy_version: v}))} placeholder="Vacío = no se exige" />
            <Field label="Fecha de publicación Política de Privacidad" value={legal.privacy_policy_updated_at}
              onChange={v => setLegal(l => ({...l, privacy_policy_updated_at: v}))} placeholder="2026-09-01" />
            <Field label="URL SLA" value={legal.sla_url}
              onChange={v => setLegal(l => ({...l, sla_url: v}))} placeholder="https://openbackup.ar/sla" />
            <Field label="Versión vigente SLA" value={legal.sla_version}
              onChange={v => setLegal(l => ({...l, sla_version: v}))} placeholder="Vacío = no se exige" />
            <Field label="Fecha de publicación SLA" value={legal.sla_updated_at}
              onChange={v => setLegal(l => ({...l, sla_updated_at: v}))} placeholder="2026-09-01" />
            <Btn label={savingLegal ? 'GUARDANDO...' : 'GUARDAR'} accent onClick={saveLegal} disabled={savingLegal} />
          </Card>

          <Card title="Email / SMTP">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
              <Field label="Servidor SMTP" value={smtp.host} onChange={v => setSmtp(s => ({...s, host: v}))} placeholder="smtp.gmail.com" />
              <Field label="Puerto" type="number" value={smtp.port} onChange={v => setSmtp(s => ({...s, port: v}))} />
            </div>
            <Field label="Usuario" value={smtp.user} onChange={v => setSmtp(s => ({...s, user: v}))} placeholder="notificaciones@empresa.com" />
            <Field label="Contraseña" type="password" value={smtp.password} onChange={v => setSmtp(s => ({...s, password: v}))} />
            <Field label="Dirección remitente (FROM)" value={smtp.from} onChange={v => setSmtp(s => ({...s, from: v}))} placeholder="backup@taxco.ar" />
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 14, marginTop: -10 }}>Debe ser una dirección de email válida, no un nombre.</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', marginBottom: 12, borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13 }}>Usar TLS (STARTTLS)</span>
              <div onClick={() => setSmtp(s => ({...s, use_tls: !s.use_tls}))} style={{
                width: 36, height: 20, borderRadius: 10, cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                background: smtp.use_tls ? 'var(--accent)' : 'var(--border)', position: 'relative',
              }}>
                <div style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s', left: smtp.use_tls ? 18 : 2 }} />
              </div>
            </div>
            <Btn label={savingSmtp ? 'GUARDANDO...' : 'GUARDAR'} accent onClick={saveSmtp} disabled={savingSmtp} />
            <Btn label={testingSmtp ? 'PROBANDO...' : 'PROBAR CONEXIÓN'} onClick={testSmtp} disabled={testingSmtp} />
          </Card>
        </div>

        <div>
          <Card title="Políticas por Defecto">
            <Field label="Retención (días)" type="number" value={defaults.retention_days} onChange={v => setDefaults(d => ({...d, retention_days: parseInt(v)}))} />
            <Field label="Hora backup full (UTC-3)" value={defaults.backup_hour} onChange={v => setDefaults(d => ({...d, backup_hour: v}))} />
            <Field label="Hora resumen diario (UTC-3)" value={defaults.daily_summary_hour} onChange={v => setDefaults(d => ({...d, daily_summary_hour: v}))} placeholder="08:00" />
            <Field label="Timeout zombie (horas)" type="number" value={defaults.zombie_timeout_hours} onChange={v => setDefaults(d => ({...d, zombie_timeout_hours: parseInt(v) || 4}))} />
            <Field label="Ventana máx. por run (minutos, vacío = sin límite)" type="number"
              value={defaults.default_max_duration_minutes}
              onChange={v => setDefaults(d => ({...d, default_max_duration_minutes: v}))}
              placeholder="vacío = sin límite" />
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16, marginTop: -8 }}>
              Ej: 480 = 8 horas, 90 = 1 hora y media
            </div>
            <Field label="Tamaño de tramo por defecto (GiB)" type="number"
              value={defaults.initial_load_default_tranche_gib}
              onChange={v => setDefaults(d => ({...d, initial_load_default_tranche_gib: v}))}
              placeholder="vacío = 10 GiB" />
            {gibToGbHelpText(defaults.initial_load_default_tranche_gib) && (
              <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 4, marginTop: -12 }}>
                {gibToGbHelpText(defaults.initial_load_default_tranche_gib)}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16, marginTop: -8 }}>
              Tamaño de cada tramo de una carga inicial, para los jobs sin un tamaño propio configurado. Vacío = 10 GiB.
            </div>
            <Field label="Días razonables para una carga inicial" type="number"
              value={defaults.initial_load_max_reasonable_days}
              onChange={v => setDefaults(d => ({...d, initial_load_max_reasonable_days: v}))}
              placeholder="vacío = 90 días" />
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16, marginTop: -8 }}>
              Si a la velocidad medida una carga inicial tardaría más que esto, se avisa al operador. Vacío = 90 días.
            </div>
            <Field label="Capacidad total contratada (GB)" type="number" value={defaults.total_storage_gb} onChange={v => setDefaults(d => ({...d, total_storage_gb: parseInt(v) || 0}))} placeholder="0" />
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16, marginTop: -8 }}>
              Storage total de OpenBackup (suma de todos los planes contratados)
            </div>
            <Field label="Tarifa Wasabi (USD/TB/mes)" type="number" value={defaults.wasabi_rate_usd_per_tb} onChange={v => setDefaults(d => ({...d, wasabi_rate_usd_per_tb: v}))} placeholder="6.99" />
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16, marginTop: -8 }}>
              Tarifa de storage de Wasabi; alimenta el costo estimado del panel de consumo real.
            </div>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', margin: '8px 0 12px' }}>Retención y limpieza de datos</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="TTL listados snapshot (horas)" type="number" value={defaults.snapshot_listing_ttl_hours} onChange={v => setDefaults(d => ({...d, snapshot_listing_ttl_hours: v}))} placeholder="48" />
              <Field label="Cancelar comandos trabados (horas)" type="number" value={defaults.job_command_stuck_hours} onChange={v => setDefaults(d => ({...d, job_command_stuck_hours: v}))} placeholder="2" />
              <Field label="Retención comandos (días)" type="number" value={defaults.job_command_retention_days} onChange={v => setDefaults(d => ({...d, job_command_retention_days: v}))} placeholder="30" />
              <Field label="Retención corridas (días)" type="number" value={defaults.job_run_retention_days} onChange={v => setDefaults(d => ({...d, job_run_retention_days: v}))} placeholder="365" />
              <Field label="Retención auditoría (días)" type="number" value={defaults.audit_log_retention_days} onChange={v => setDefaults(d => ({...d, audit_log_retention_days: v}))} placeholder="730" />
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16, marginTop: 4 }}>
              La limpieza corre una vez por día. Borrar corridas viejas elimina también sus logs.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', marginBottom: 16, borderTop: '1px solid var(--border)' }}>
              <div>
                <span style={{ fontSize: 13 }}>Email de bienvenida a nuevos clientes</span>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, fontFamily: 'var(--mono)' }}>Requiere SMTP configurado</div>
              </div>
              <div onClick={() => setDefaults(d => ({...d, welcome_email_enabled: !d.welcome_email_enabled}))} style={{
                width: 36, height: 20, borderRadius: 10, cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                background: defaults.welcome_email_enabled ? 'var(--accent)' : 'var(--border)', position: 'relative',
              }}>
                <div style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s', left: defaults.welcome_email_enabled ? 18 : 2 }} />
              </div>
            </div>
            <Btn label={savingDefaults ? 'GUARDANDO...' : 'GUARDAR'} accent onClick={saveDefaults} disabled={savingDefaults} />
          </Card>

          <Card title="Seguridad — Política MFA">
            {[
              { role: 'admin',    label: 'MFA obligatorio para Administradores' },
              { role: 'operator', label: 'MFA obligatorio para Operadores' },
              { role: 'client',   label: 'MFA obligatorio para Clientes' },
              { role: 'reseller', label: 'MFA obligatorio para Resellers' },
            ].map(({ role, label }) => (
              <div key={role} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13 }}>{label}</span>
                <div onClick={() => toggleMfa(role)} style={{
                  width: 36, height: 20, borderRadius: 10, cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                  background: mfaPolicy[role] ? 'var(--accent)' : 'var(--border)', position: 'relative',
                }}>
                  <div style={{
                    position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%',
                    background: 'white', transition: 'left 0.2s',
                    left: mfaPolicy[role] ? 18 : 2,
                  }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
              Los usuarios del rol seleccionado deberán configurar MFA en su próximo login.
            </div>
          </Card>

          <Card title="Planes Disponibles">
            {plans.map((p, i) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: i < plans.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2,
                  background: p.active ? 'rgba(0,212,255,0.12)' : 'rgba(100,116,139,0.12)',
                  color: p.active ? 'var(--accent)' : 'var(--muted)' }}>{p.name}</span>
                <span style={{ flex: 1, fontSize: 13 }}>{p.included_storage_gb >= 99999 ? 'Custom' : (p.included_storage_gb || 0) + ' GB'} · ${(p.base_price_usd || 0).toFixed(2)}/mes</span>
                <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: p.active ? 'var(--green)' : 'var(--muted)' }}>{p.active ? 'ACTIVO' : 'INACTIVO'}</span>
                <button onClick={() => setPlanModal(p)}
                  style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>EDITAR</button>
              </div>
            ))}
            <button onClick={() => setPlanModal('new')}
              style={{ marginTop: 12, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>+ NUEVO PLAN</button>
          </Card>

          <Card title="Latidos del Scheduler">
            {heartbeats.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Sin datos todavía.</div>
            )}
            {heartbeats.map((h, i) => (
              <div key={h.task_id} style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 0',
                borderBottom: i < heartbeats.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <span style={{ flex: '1 1 220px', fontSize: 13, fontFamily: 'var(--mono)' }}>{h.task_id}</span>
                <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                  {h.last_success_at ? `último OK: ${new Date(h.last_success_at).toLocaleString('es-AR')}` : 'nunca corrió'}
                </span>
                <span style={{
                  display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)',
                  letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2,
                  background: h.alert_active ? 'rgba(255,77,109,0.12)' : 'rgba(34,197,94,0.12)',
                  color: h.alert_active ? 'var(--red)' : 'var(--green)',
                }}>{h.alert_active ? 'SILENCIOSA' : 'OK'}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
      {systemInfo && (
        <div style={{ marginTop: 16, fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', overflowWrap: 'anywhere' }}>
          {systemSummaryLine(systemInfo)}
        </div>
      )}
    </div>
  );
}
